import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useMedia } from 'react-use';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import mileageIcon from 'public/static/images/mileage-icon.svg';
import drivetrainRwdIcon from 'public/static/images/drivetrain-icon-rwd.svg';
import drivetrainFwdIcon from 'public/static/images/drivetrain-icon-fwd.svg';
import drivetrainAwdIcon from 'public/static/images/drivetrain-icon-awd.svg';
import manualTransmissionIcon from 'public/static/images/manual-transmission-icon.svg';
import automaticTransmissionIcon from 'public/static/images/automatic-transmission-icon.svg';
import exteriorColorIcon from 'public/static/images/exterior-color-icon.svg';
import interioColorIcon from 'public/static/images/interior-color-icon.svg';
import fuelTypeGasolineIcon from 'public/static/images/fuel-type-gasoline-icon.svg';
import fuelTypeHybridIcon from 'public/static/images/fuel-type-hybrid-icon.svg';
import fuelTypeElectricIcon from 'public/static/images/fuel-type-electric-icon.svg';
import arrow from 'public/static/images/arrow-right-red.svg';
import mpgIcon from 'public/static/images/mpg-icon.svg';
import closeButton from 'public/static/images/close-pill.svg';
import noImages from 'public/static/images/srp/no-images.png';

import { screen } from 'components/styles/variables';
import { formatPrice, formatMileage } from 'components/Srp/SearchResultCard/utils';
import useMonthlyPayment from 'lib/hooks/useMonthlyPayment';
import FavoriteIcon from 'components/FavoriteIcon';
import useFavorite from 'lib/hooks/useFavorite';
import { VehicleInfo } from 'types/Apis/VehicleApiResponse';
import useHashLocation from 'lib/hooks/useHashLocation';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import useEvents from 'lib/hooks/useEvents';
import SrpTileViewMoreEvent from 'lib/Tracking/Srp/SrpTileViewMoreEvent';
import SrpTileFavoriteEvent from 'lib/Tracking/Srp/SrpTileFavoriteEvent';

import { srpTileStyles, styles } from './styles';

interface SrpTileProps {
  isModalOpen: boolean;
  vehicle: VehicleInfo;
  onClose: () => void;
}

export default function SrpTile({ isModalOpen, vehicle, onClose }: SrpTileProps): JSX.Element {
  const { favorite, isFavorited, unfavorite } = useFavorite(vehicle?.vin);
  const [favoriteState, setFavoriteState] = useState(false);
  const [vdpUrl, setVdpUrl] = useState('');
  const isDesktop = useMedia(`(min-width: ${screen.smMin})`);
  const { values, cleanKeys } = useHashLocation();
  const { isMobile } = useDeviceScreen();
  const logger = useEvents();
  const {
    price,
    mileage,
    transmission,
    driveline,
    mpg,
    make,
    model,
    year,
    trim,
    vin,
    exteriorColor,
    interiorColor,
    fuelType,
    photoUrls,
    priceFormatted,
    doors,
    bodyStyle,
  } = vehicle;
  const { t } = useTranslation();
  const imageUrl = photoUrls?.length ? photoUrls[0] : noImages;
  const monthlyPayment = useMonthlyPayment(price);

  useEffect(() => {
    if (isFavorited && !favoriteState) {
      setFavoriteState(true);
    }

    if (!isFavorited && favoriteState) {
      setFavoriteState(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFavorited]);

  useEffect(() => {
    setVdpUrl(
      `${window.location.origin}${window.location.pathname}#${cleanKeys({
        ...values,
        vin,
      })}`
    );
  }, [cleanKeys, values, vin, isMobile]);

  function onToggleFavorite(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    logger.log(SrpTileFavoriteEvent());
    setFavoriteState((state) => !state);
    e.preventDefault();
    e.stopPropagation();
    if (isFavorited) {
      unfavorite();
    } else {
      favorite();
    }
  }

  function onLogViewMore(): void {
    logger.log(SrpTileViewMoreEvent());
  }

  const drivetrainIcons: Record<string, string> = {
    FWD: drivetrainFwdIcon,
    RWD: drivetrainRwdIcon,
    AWD: drivetrainAwdIcon,
    '4X4': drivetrainAwdIcon,
  };

  const drivetrainIcon = drivetrainIcons[driveline];

  const fuelTypeIcon: Record<string, string> = {
    electric: fuelTypeElectricIcon,
    diesel: fuelTypeGasolineIcon,
    gasoline: fuelTypeGasolineIcon,
    hybrid: fuelTypeHybridIcon,
    'hydrogen fuel cell': fuelTypeElectricIcon,
    'natural gas': fuelTypeGasolineIcon,
    'plug-in hybrid': fuelTypeHybridIcon,
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        style={srpTileStyles(isDesktop)}
        htmlOpenClassName="html-modal-open"
        shouldCloseOnOverlayClick={true}
        onRequestClose={onClose}
      >
        <div>
          <button aria-label="close" className="close-button" onClick={onClose}>
            <Image src={closeButton} width={16} height={16} alt="back" />
          </button>
          <div className="border" />
          <div
            className="overview-card"
            role="dialog"
            id="radix-12"
            aria-labelledby="radix-13"
            tabIndex={-1}
          >
            <div className="overview-image-container">
              <Image
                src={imageUrl}
                height={68}
                width={113}
                alt={`${year} ${make} ${model} ${trim}`}
                style={{ borderRadius: '4px', objectFit: 'cover' }}
              />
            </div>
            <div className="full-width">
              <div className="make-model">
                {year} {make} {model} {trim}
              </div>
              <span>
                <span className="price">{priceFormatted}</span>
                <span className="est-payment">est. {formatPrice(monthlyPayment)}/mo.</span>
              </span>
            </div>
            <FavoriteIcon
              onClick={onToggleFavorite}
              active={favoriteState}
              className="favorite-button"
            />
          </div>
          <header className="section-header">
            <h3 className="section-title">{t(`srpTile.keyDetails`)}</h3>
            <div className="view-more">
              <a href={vdpUrl} onClick={onLogViewMore}>
                {t(`srpTile.viewMore`)}
                <Image
                  className="view-more-arrow"
                  src={arrow}
                  alt="view-more-arrow"
                  width={15}
                  height={11}
                />
              </a>
            </div>
          </header>

          <div className="key-details">
            <ul>
              <div className="detail-row">
                <li className="detail-section">
                  <div>
                    <div>
                      <Image src={mileageIcon} alt="Mileage" width={44} height={44} />
                    </div>
                  </div>
                  <div>
                    <h4 className="detail-title">{t(`srpTile.mileage`)}</h4>
                    <p className="detail-content">{formatMileage(mileage)}</p>
                  </div>
                </li>
                <li className="detail-section">
                  <div>
                    <div>
                      <Image
                        src={
                          transmission === 'automatic'
                            ? automaticTransmissionIcon
                            : manualTransmissionIcon
                        }
                        alt="Transmission"
                        width={44}
                        height={44}
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="detail-title">{t(`srpTile.transmission`)}</h4>
                    <p className="detail-content">{transmission}</p>
                  </div>
                </li>
              </div>
              <div className="detail-row">
                <li className="detail-section">
                  <div>
                    <div>
                      <Image
                        src={drivetrainAwdIcon ?? drivetrainIcon}
                        alt="Drivetrain"
                        width={44}
                        height={44}
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="detail-title">{t(`srpTile.drivetrain`)}</h4>
                    <p className="detail-content">{driveline}</p>
                  </div>
                </li>
                <li className="detail-section">
                  <div>
                    <div>
                      <Image src={mpgIcon} alt="MPG" width={44} height={44} />
                    </div>
                  </div>
                  <div>
                    <h4 className="detail-title">{t(`srpTile.mpg`)}</h4>
                    <p className="detail-content">{mpg}</p>
                  </div>
                </li>
              </div>
              <div className="detail-row">
                <li className="detail-section">
                  <div>
                    <div>
                      <Image src={exteriorColorIcon} alt="Exterior color" width={44} height={44} />
                    </div>
                  </div>
                  <div>
                    <h4 className="detail-title">{t(`srpTile.exteriorColor`)}</h4>
                    <p className="detail-content">{exteriorColor}</p>
                  </div>
                </li>
                <li className="detail-section">
                  <div>
                    <div>
                      <Image
                        src={fuelTypeIcon[fuelType] ?? fuelTypeGasolineIcon}
                        alt="Fuel type"
                        width={44}
                        height={44}
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="detail-title">{t(`srpTile.fuelType`)}</h4>
                    <p className="detail-content">{fuelType}</p>
                  </div>
                </li>
              </div>
              <div className="detail-row">
                <li className="detail-section">
                  <div>
                    <div>
                      <Image src={interioColorIcon} alt="Interior color" width={44} height={44} />
                    </div>
                  </div>
                  <div>
                    <h4 className="detail-title">{t(`srpTile.interiorColor`)}</h4>
                    <p className="detail-content">{interiorColor}</p>
                  </div>
                </li>
              </div>
            </ul>
          </div>
        </div>

        <div className="vehicle-information">
          <div>
            <header className="section-header">
              <h3 className="section-title">{t(`srpTile.vehicleInformation`)}</h3>
              <a href={vdpUrl} onClick={onLogViewMore}>
                {t(`srpTile.viewMore`)}
                <Image
                  className="view-more-arrow"
                  src={arrow}
                  alt="view-more-arrow"
                  width={15}
                  height={11}
                />
              </a>
            </header>
            <div>
              <div className="information-row">
                <div className="information-section">
                  <span className="information-title">{t(`srpTile.make`)}:</span>
                  <span className="information-content" title={make}>
                    {make}
                  </span>
                </div>
                <div className="information-section">
                  <span className="information-title">{t(`srpTile.bodyStyle`)}:</span>
                  <span className="information-content" title={bodyStyle}>
                    {bodyStyle}
                  </span>
                </div>
              </div>
              <div className="information-row">
                <div className="information-section">
                  <span className="information-title">{t(`srpTile.model`)}:</span>
                  <span className="information-content" title={model}>
                    {model}
                  </span>
                </div>
                <div className="information-section">
                  <span className="information-title">{t(`srpTile.doors`)}:</span>
                  <span className="information-content" title={doors?.toString()}>
                    {doors}
                  </span>
                </div>
              </div>
              <div className="information-row">
                <div className="information-section">
                  <span className="information-title">{t(`srpTile.trim`)}:</span>
                  <span className="information-content" title={trim}>
                    {trim}
                  </span>
                </div>
                <div className="information-section">
                  <span className="information-title">{t(`srpTile.vin`)}:</span>
                  <span className="information-content" title={vin}>
                    {vin}
                  </span>
                </div>
              </div>
              <div className="information-row">
                <div className="information-section">
                  <span className="information-title">{t(`srpTile.year`)}:</span>
                  <span className="information-content" title={year?.toString()}>
                    {year}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <style>{styles}</style>
    </>
  );
}
